import { Box, TableRow } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonTableApp,
  TableCellApp,
  TableModuleApp,
  TypographyTableApp,
} from '../../components/custom/CustomComponents';
import { Context } from '../../service/Context';
import { capitalizar, ordenarLista } from '../../utils/UtilesComun';

/**
 * Columnas de la tabla de máximos goleadores.
 */
const columns = [
  { field: 'nombreJugador', label: '', width: '46.5%', sortable: false },
  { field: 'nombreEquipo', label: '', width: '46.5%', sortable: false },
  { field: 'goles', label: '', width: '7%', tooltip: 'Goles', sortable: false, sx: { textAlign: 'center' } },
];

/**
 * Fila de la tabla de máximos goleadores.
 */
const FilaGoleador = ({ jugadorLiga, columns, estaEnAlgunaLista, index }) => {
  const navigate = useNavigate();

  return (
    <TableRow key={jugadorLiga.idJugador}>
      {columns.map((col) => {
        if (col.field === 'nombreJugador') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                enLista={estaEnAlgunaLista}
                onClick={() => navigate(`/jugador/${jugadorLiga.idJugador}`)}
                text={capitalizar(jugadorLiga.nombreJugador)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'nombreEquipo') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                onClick={() => navigate(`/equipo/${jugadorLiga.idEquipo}`)}
                text={capitalizar(jugadorLiga.nombreEquipo)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'goles') {
          const esPrimero = index === 0;
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: 0 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 22,
                  px: 0.5,
                  borderRadius: '6px',
                  backgroundColor: esPrimero ? '#ecfdf5' : '#f1f5f9',
                  color: esPrimero ? '#047857' : '#334155',
                  border: `1px solid ${esPrimero ? '#a7f3d0' : '#cbd5e1'}`,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  boxShadow: esPrimero ? '0 1px 3px rgba(16, 185, 129, 0.15)' : '0 1px 2px rgba(0, 0, 0, 0.04)',
                }}
              >
                {jugadorLiga.goles}
              </Box>
            </TableCellApp>
          );
        }

        return <TypographyTableApp key={col.field}>{jugadorLiga[col.field]}</TypographyTableApp>;
      })}
    </TableRow>
  );
};

/**
 * Lista de máximos goleadores de una liga en formato de tabla.
 */
const MaximosGoleadores = ({ datosLiga, maximosGoleadores, loading = false }) => {
  const { obtenerListasConJugador } = useContext(Context);
  const [listasCache, setListasCache] = useState({});
  const [sort, setSort] = useState({ field: 'goles', direction: 'desc' });

  const goleadoresOrdenados = useMemo(() => {
    return maximosGoleadores ? ordenarLista(maximosGoleadores, sort) : [];
  }, [maximosGoleadores, sort]);

  useEffect(() => {
    async function cargarEstadoListas() {
      if (!Array.isArray(maximosGoleadores)) {
        return;
      }
      const promesas = maximosGoleadores.map(async (j) => {
        if (listasCache[j.idJugador] !== undefined) {
          return null;
        }
        const listas = await obtenerListasConJugador(j.idJugador);
        return { id: j.idJugador, enLista: listas.length > 0 };
      });
      const resultados = (await Promise.all(promesas)).filter((r) => r !== null);
      if (resultados.length > 0) {
        const nuevoCache = { ...listasCache };
        resultados.forEach((r) => (nuevoCache[r.id] = r.enLista));
        setListasCache(nuevoCache);
      }
    }
    cargarEstadoListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maximosGoleadores]);

  const handleListasChange = (idJugador, listas) => {
    setListasCache((prev) => ({ ...prev, [idJugador]: listas.length > 0 }));
  };

  if (!datosLiga && !loading) {
    return null;
  }
  if (!maximosGoleadores && !loading) {
    return null;
  }

  return (
    <TableModuleApp
      noHeader
      title="Máximos Goleadores"
      columns={columns}
      sort={sort}
      setSort={setSort}
      loading={loading}
      data={goleadoresOrdenados}
      renderRow={(jugador, index) => (
        <FilaGoleador
          key={`${jugador.idJugador}-${index}`}
          index={index}
          jugadorLiga={jugador}
          columns={columns}
          estaEnAlgunaLista={listasCache[jugador.idJugador] || false}
          handleListasChange={(listas) => handleListasChange(jugador.idJugador, listas)}
        />
      )}
    />
  );
};

MaximosGoleadores.propTypes = {
  datosLiga: PropTypes.object,
  maximosGoleadores: PropTypes.array,
  loading: PropTypes.bool,
};

FilaGoleador.propTypes = {
  jugadorLiga: PropTypes.object,
  columns: PropTypes.array,
  estaEnAlgunaLista: PropTypes.bool,
  handleListasChange: PropTypes.func,
  index: PropTypes.number,
};

export default MaximosGoleadores;
